// 006 — Epicrisis (Fase 7, Tanda 11, ver PLAN.md). Transcripción campo por campo del PDF oficial del Anexo 1 MSP: hojas
// idx 23-24 (footer `SNS-MSP/HCU-form.006/2021`, "EPICRISIS (1)" y "(2)"). Informe de brechas en
// `docs/hce-fase7/brechas-002-003-005-006-007.md`.
//
// Retirado de la UI por no estar impreso: "Título del documento" (el `Composition.title` queda fijo "Epicrisis") y
// "Medicación al alta"/"Plan de seguimiento" como campos propios (el papel los cubre con "Indicaciones de alta" y
// "Próximo control"). Decisiones ante el papel: la condición de alta (H) son casillas independientes tal como se
// imprimen, sin validar consistencia entre ellas (el papel no dibuja a qué grupo pertenece cada una); "Diagnóstico
// secundario" son 5 filas (contadas en la imagen).
//
// FHIR (consumidores: `chequeoCompletitudConsulta`, `calidad-hc`, `enfermeria.esNotaSoapie`, AccessPolicy de
// enfermería): se conserva el `Composition` con `type` LOINC 18842-5, `status` final, `encounter` y `author`. Las
// secciones ahora siguen los bloques oficiales; ningún consumidor lee sus títulos.
import { conCodigoCie, paresDeCampos, valoresVacios, type CampoSimple, type Valores } from './camposSimplesDatos';
import type { DiagnosticoCie10Valor } from './SelectorDiagnosticoCie10';
import { estadoInicialTablaConColumnas, resumenFilaTablaConColumnas, type ColumnaTablaConColumnas, type EstadoTablaConColumnas, type FilaTablaConColumnas } from './uci/TablaConColumnas';
import type { Par } from './index';

const casilla = (clave: string, etiqueta: string): CampoSimple => ({ clave, etiqueta, tipo: 'casilla' });
const area = (clave: string, etiqueta: string): CampoSimple => ({ clave, etiqueta, tipo: 'area' });

export const CAMPOS_CUADRO_006: readonly CampoSimple[] = [area('cuadroClinico', 'Resumen del cuadro clínico')];
export const CAMPOS_EVOLUCION_006: readonly CampoSimple[] = [area('evolucion', 'Resumen de evolución y complicaciones')];
export const CAMPOS_HALLAZGOS_006: readonly CampoSimple[] = [area('hallazgos', 'Hallazgos relevantes de exámenes y procedimientos diagnósticos')];
export const CAMPOS_TRATAMIENTO_006: readonly CampoSimple[] = [area('tratamiento', 'Resumen de tratamiento y procedimientos terapéuticos')];
export const CAMPOS_INDICACIONES_006: readonly CampoSimple[] = [area('indicaciones', 'Indicaciones de alta/egreso'), { clave: 'proximoControl', etiqueta: 'Próximo control' }];

export const NUM_SECUNDARIOS_006 = 5;

export const CAMPOS_CONDICION_006: readonly CampoSimple[] = [
  casilla('vivo', 'Vivo'),
  casilla('fallecido', 'Fallecido'),
  casilla('altaMedica', 'Alta médica'),
  casilla('altaVoluntaria', 'Alta voluntaria'),
  casilla('asintomatico', 'Asintomático'),
  casilla('discapacidad', 'Discapacidad'),
  casilla('retiroNoAutorizado', 'Retiro no autorizado'),
  casilla('defuncionMenos48', 'Defunción menos de 48 horas'),
  casilla('defuncionMas48', 'Defunción más de 48 horas'),
  { clave: 'diasEstada', etiqueta: 'Días de estada', tipo: 'numero' },
  { clave: 'diasReposo', etiqueta: 'Días de reposo', tipo: 'numero' },
];

export const FILAS_MEDICOS_006: readonly FilaTablaConColumnas[] = Array.from({ length: 4 }, (_, i) => ({ clave: `f${i + 1}`, etiqueta: String(i + 1) }));
export const COLUMNAS_MEDICOS_006: readonly ColumnaTablaConColumnas[] = [
  { clave: 'nombre', etiqueta: 'Nombre y apellidos' },
  { clave: 'especialidad', etiqueta: 'Especialidad' },
  { clave: 'documento', etiqueta: 'Sello y número de documento de identificación del profesional' },
  { clave: 'periodo', etiqueta: 'Período de responsabilidad' },
];

export const CAMPOS_ELABORACION_006: readonly CampoSimple[] = [
  { clave: 'elaboradoPor', etiqueta: 'Elaborado por' },
  { clave: 'revisadoPor', etiqueta: 'Revisado por' },
];

export interface Estado006 {
  readonly cuadro: Valores;
  readonly evolucion: Valores;
  readonly hallazgos: Valores;
  readonly tratamiento: Valores;
  readonly indicaciones: Valores;
  readonly principal: DiagnosticoCie10Valor;
  readonly secundarios: readonly DiagnosticoCie10Valor[];
  readonly causaExterna: DiagnosticoCie10Valor;
  readonly condicion: Valores;
  readonly medicos: EstadoTablaConColumnas;
  readonly elaboracion: Valores;
}

const dxVacio = (): DiagnosticoCie10Valor => ({ texto: '', cie10: null });

export function estadoInicial006(): Estado006 {
  return {
    cuadro: valoresVacios(CAMPOS_CUADRO_006),
    evolucion: valoresVacios(CAMPOS_EVOLUCION_006),
    hallazgos: valoresVacios(CAMPOS_HALLAZGOS_006),
    tratamiento: valoresVacios(CAMPOS_TRATAMIENTO_006),
    indicaciones: valoresVacios(CAMPOS_INDICACIONES_006),
    principal: dxVacio(),
    secundarios: Array.from({ length: NUM_SECUNDARIOS_006 }, dxVacio),
    causaExterna: dxVacio(),
    condicion: valoresVacios(CAMPOS_CONDICION_006),
    medicos: estadoInicialTablaConColumnas(FILAS_MEDICOS_006, COLUMNAS_MEDICOS_006),
    elaboracion: valoresVacios(CAMPOS_ELABORACION_006),
  };
}

/** Secciones narrativas de la `Composition`, una por bloque oficial con contenido (título = rótulo del papel). */
export function seccionesEpicrisis006(e: Estado006): { readonly titulo: string; readonly texto: string }[] {
  const dx = [
    conCodigoCie(e.principal) ? `Diagnóstico principal: ${conCodigoCie(e.principal)}` : '',
    ...e.secundarios.map((d, i) => (conCodigoCie(d) ? `Diagnóstico secundario ${i + 1}: ${conCodigoCie(d)}` : '')),
    conCodigoCie(e.causaExterna) ? `Causa externa: ${conCodigoCie(e.causaExterna)}` : '',
  ].filter(Boolean);
  const condicion = paresDeCampos(CAMPOS_CONDICION_006, e.condicion)
    .filter((p) => p.respuesta.trim())
    .map((p) => (p.respuesta === 'Sí' ? p.pregunta : `${p.pregunta}: ${p.respuesta}`));
  const indicaciones = [(e.indicaciones.indicaciones ?? '').trim(), (e.indicaciones.proximoControl ?? '').trim() ? `Próximo control: ${e.indicaciones.proximoControl!.trim()}` : ''].filter(Boolean);
  return [
    { titulo: 'Resumen del cuadro clínico', texto: (e.cuadro.cuadroClinico ?? '').trim() },
    { titulo: 'Resumen de evolución y complicaciones', texto: (e.evolucion.evolucion ?? '').trim() },
    { titulo: 'Hallazgos relevantes de exámenes y procedimientos diagnósticos', texto: (e.hallazgos.hallazgos ?? '').trim() },
    { titulo: 'Resumen de tratamiento y procedimientos terapéuticos', texto: (e.tratamiento.tratamiento ?? '').trim() },
    { titulo: 'Indicaciones de alta/egreso', texto: indicaciones.join('\n') },
    { titulo: 'Diagnóstico de alta/egreso', texto: dx.join('\n') },
    { titulo: 'Condición de alta/egreso', texto: condicion.join(', ') },
  ].filter((s) => s.texto);
}

export function paresDe006(e: Estado006): Par[] {
  const dx = (titulo: string, d: DiagnosticoCie10Valor): Par => ({ pregunta: titulo, respuesta: conCodigoCie(d) });
  return [
    ...paresDeCampos(CAMPOS_CUADRO_006, e.cuadro),
    ...paresDeCampos(CAMPOS_EVOLUCION_006, e.evolucion),
    ...paresDeCampos(CAMPOS_HALLAZGOS_006, e.hallazgos),
    ...paresDeCampos(CAMPOS_TRATAMIENTO_006, e.tratamiento),
    ...paresDeCampos(CAMPOS_INDICACIONES_006, e.indicaciones),
    dx('Diagnóstico principal', e.principal),
    ...e.secundarios.map((d, i) => dx(`Diagnóstico secundario ${i + 1}`, d)),
    dx('Causa externa', e.causaExterna),
    ...paresDeCampos(CAMPOS_CONDICION_006, e.condicion, 'Condición de alta — '),
    ...FILAS_MEDICOS_006.map((f) => ({ pregunta: `Médicos tratantes — ${f.etiqueta}`, respuesta: resumenFilaTablaConColumnas(e.medicos, f.clave, COLUMNAS_MEDICOS_006) })),
    ...paresDeCampos(CAMPOS_ELABORACION_006, e.elaboracion),
  ].filter((p) => p.respuesta.trim() !== '');
}
